import { useEffect, useState } from 'react';
import { watchInstallPrompt } from '../../portal/lib/pwa';

/**
 * PWA install + Web Push helpers for the Staff Companion. Mirrors
 * portal/lib/pwa.js's approach (head tags + service worker registration +
 * beforeinstallprompt capture) but for the `/staff-app` scope. Reuses
 * `watchInstallPrompt` as-is — that part has no portal-specific logic.
 */

const HEAD_TAGS = [
  { tag: 'meta', attrs: { name: 'theme-color', content: '#12161c' } },
  { tag: 'meta', attrs: { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-capable', content: 'yes' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-title', content: 'Staff Companion' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' } },
];

const PHARMACY_KEY = 'dutilyne_staff_pharmacy';
const PHARMACY_NAME_KEY = 'dutilyne_staff_pharmacy_name';

export const rememberStaffPharmacy = (code, name) => {
  if (code) localStorage.setItem(PHARMACY_KEY, code);
  if (name) localStorage.setItem(PHARMACY_NAME_KEY, name);
};
export const staffPharmacyCode = () => localStorage.getItem(PHARMACY_KEY) || '';
export const staffPharmacyName = () => localStorage.getItem(PHARMACY_NAME_KEY) || '';

/** Manifest + home-screen icon: the pharmacy's own branded set when we know
 * which pharmacy this install belongs to, otherwise the shared DutiLyne one. */
const brandedTags = (code) => (code ? [
  { tag: 'link', attrs: { rel: 'manifest', href: `/api/staff-app/manifest/${encodeURIComponent(code)}.webmanifest` } },
  { tag: 'link', attrs: { rel: 'apple-touch-icon', sizes: '180x180', href: `/api/staff-app/manifest/${encodeURIComponent(code)}/icon/180.png` } },
] : [
  { tag: 'link', attrs: { rel: 'manifest', href: '/staff-manifest.webmanifest' } },
  { tag: 'link', attrs: { rel: 'apple-touch-icon', sizes: '180x180', href: '/staff-icon-180.png' } },
]);

const MARK = 'data-staffapp-pwa';
const OVERRIDE_META = new Set(['viewport', 'theme-color', 'apple-mobile-web-app-capable', 'apple-mobile-web-app-title', 'apple-mobile-web-app-status-bar-style']);

export function installHeadTags(pharmacyCode = staffPharmacyCode()) {
  const previousTitle = document.title;
  const created = [];
  const overrides = [];

  [...HEAD_TAGS, ...brandedTags(pharmacyCode)].forEach(({ tag, attrs }) => {
    if (tag === 'meta' && OVERRIDE_META.has(attrs.name)) {
      const existing = document.querySelector(`meta[name="${attrs.name}"]`);
      if (existing) {
        overrides.push({ el: existing, prevContent: existing.getAttribute('content') });
        existing.setAttribute('content', attrs.content);
        return;
      }
    }
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute(MARK, '1');
    document.head.appendChild(el);
    created.push(el);
  });
  document.title = 'DutiLyne Staff Companion';

  return () => {
    created.forEach((el) => el.remove());
    overrides.forEach(({ el, prevContent }) => {
      if (prevContent === null) el.removeAttribute('content');
      else el.setAttribute('content', prevContent);
    });
    document.title = previousTitle;
  };
}

let swRegistered = false;
export async function registerStaffServiceWorker() {
  if (swRegistered) return;
  if (!('serviceWorker' in navigator)) return;
  try {
    await navigator.serviceWorker.register('/staff-sw.js', { scope: '/staff-app' });
    swRegistered = true;
  } catch (err) {
    console.warn('Staff Companion SW registration failed:', err?.message || err);
  }
}

export function useStaffAppInstallPrompt() {
  const [installTrigger, setInstallTrigger] = useState(null);
  useEffect(() => watchInstallPrompt((trigger) => setInstallTrigger(() => trigger), () => setInstallTrigger(null)), []);
  const install = async () => { if (installTrigger && await installTrigger()) setInstallTrigger(null); };
  return { canInstall: !!installTrigger, install };
}

// ─── Web Push ────────────────────────────────────────────────────────────────

function urlSafeBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const outputArray = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) outputArray[i] = raw.charCodeAt(i);
  return outputArray;
}

export async function subscribeToStaffPush(api) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Push notifications are not supported in this browser');
  }
  const vapidKeyRes = await api.get('/portal/push/vapid-public-key');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlSafeBase64ToUint8Array(vapidKeyRes.data.vapid_public_key),
    });
  }
  await api.post('/staff-app/push/subscribe', sub.toJSON());
  return sub;
}

export async function unsubscribeFromStaffPush(api) {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.delete('/staff-app/push/subscribe', { params: { endpoint: sub.endpoint } });
    await sub.unsubscribe();
  }
}

export async function getStaffPushStatus() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'subscribed' : 'unknown';
  } catch {
    return 'unknown';
  }
}
